import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Controller, Control, FieldErrors, useWatch } from "react-hook-form";
import { Label, FormFeedback } from "reactstrap";

interface CommonTimePickerProps {
  control: Control<any>;
  name: string;
  label?: string;
  placeholder?: string;
  rules?: Record<string, any>;
  errors?: FieldErrors<any>;
  disabled?: boolean;
  required?: boolean;
  description?: string;
}

const HOURS_BASE = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES_BASE = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

// 3 copies for seamless infinite circular looping
const HOURS_REPEAT = [...HOURS_BASE, ...HOURS_BASE, ...HOURS_BASE];
const MINUTES_REPEAT = [...MINUTES_BASE, ...MINUTES_BASE, ...MINUTES_BASE];

const ITEM_HEIGHT = 28; // px height per row item
const VISIBLE_HEIGHT = 180; // px height of visible scroll viewport

/**
 * 24-Hour 2-Column Time Picker component with Infinite Circular Looping.
 */
const CommonTimePicker: React.FC<CommonTimePickerProps> = ({
  control,
  name,
  label,
  placeholder = "--:--",
  rules = {},
  errors = {},
  disabled = false,
  description,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLDivElement>(null);
  const minutesRef = useRef<HTMLDivElement>(null);

  const isRequired =
    typeof rules?.required === "string" || rules?.required === true;

  const val = useWatch({ control, name }) || "";
  const [selectedHour, selectedMinute] = val ? val.split(":") : ["", ""];

  // Update dynamic position relative to screen
  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      const handleScrollOrResize = () => updatePosition();
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }
  }, [isOpen]);

  // Close popover when clicking outside container or portal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        popoverRef.current &&
        !popoverRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Center selected items in the middle block when popover opens
  useEffect(() => {
    if (isOpen) {
      // Hours scroll position (Middle block offset = 24 * ITEM_HEIGHT)
      if (hoursRef.current) {
        const hIdx = HOURS_BASE.indexOf(selectedHour);
        const targetIdx = 24 + (hIdx >= 0 ? hIdx : 0);
        hoursRef.current.scrollTop = targetIdx * ITEM_HEIGHT - VISIBLE_HEIGHT / 2 + ITEM_HEIGHT / 2;
      }
      // Minutes scroll position (Middle block offset = 60 * ITEM_HEIGHT)
      if (minutesRef.current) {
        const mIdx = MINUTES_BASE.indexOf(selectedMinute);
        const targetIdx = 60 + (mIdx >= 0 ? mIdx : 0);
        minutesRef.current.scrollTop = targetIdx * ITEM_HEIGHT - VISIBLE_HEIGHT / 2 + ITEM_HEIGHT / 2;
      }
    }
  }, [isOpen, selectedHour, selectedMinute]);

  // Circular / Infinite scroll handler for Hours
  const handleHoursScroll = () => {
    const el = hoursRef.current;
    if (!el) return;
    const oneBlock = 24 * ITEM_HEIGHT;
    if (el.scrollTop < oneBlock * 0.5) {
      el.scrollTop += oneBlock;
    } else if (el.scrollTop > oneBlock * 1.5) {
      el.scrollTop -= oneBlock;
    }
  };

  // Circular / Infinite scroll handler for Minutes
  const handleMinutesScroll = () => {
    const el = minutesRef.current;
    if (!el) return;
    const oneBlock = 60 * ITEM_HEIGHT;
    if (el.scrollTop < oneBlock * 0.5) {
      el.scrollTop += oneBlock;
    } else if (el.scrollTop > oneBlock * 1.5) {
      el.scrollTop -= oneBlock;
    }
  };

  return (
    <div ref={containerRef} className="position-relative">
      {label && (
        <Label className="form-label" style={{ marginBottom: "5px" }}>
          {label}
          {isRequired && (
            <span style={{ color: "red", marginLeft: "3px" }}>*</span>
          )}
          {description && (
            <small className="text-muted d-block">{description}</small>
          )}
        </Label>
      )}

      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => {
          const fieldValue = field.value || "";
          const [currentHour, currentMinute] = fieldValue
            ? fieldValue.split(":")
            : ["", ""];

          const handleHourSelect = (h: string) => {
            const m = currentMinute || "00";
            field.onChange(`${h}:${m}`);
          };

          const handleMinuteSelect = (m: string) => {
            const h = currentHour || "00";
            field.onChange(`${h}:${m}`);
          };

          return (
            <>
              {/* Input Box matching Chrome / standard form input styling */}
              <div className="input-group input-group-sm">
                <input
                  type="text"
                  className={`form-control ${errors?.[name] ? "is-invalid" : ""}`}
                  style={{
                    height: "28px",
                    fontSize: "13px",
                    padding: "10px 14px",
                    backgroundColor: disabled ? "#e9ecef" : "#fff",
                    cursor: disabled ? "not-allowed" : "pointer",
                    borderColor: isOpen ? "#0d6efd" : undefined,
                    boxShadow: isOpen ? "0 0 0 0.2rem rgba(13, 110, 253, 0.25)" : undefined,
                  }}
                  placeholder={placeholder}
                  value={fieldValue || ""}
                  readOnly
                  disabled={disabled}
                  onClick={() => !disabled && setIsOpen(!isOpen)}
                />
                <span
                  className="input-group-text bg-white"
                  style={{
                    height: "28px",
                    padding: "0 10px",
                    cursor: disabled ? "not-allowed" : "pointer",
                    borderColor: isOpen ? "#0d6efd" : undefined,
                  }}
                  onClick={() => !disabled && setIsOpen(!isOpen)}
                >
                  <i className="ri-time-line text-dark opacity-75"></i>
                </span>
              </div>

              {/* Popover Card Portal rendered in document.body */}
              {isOpen &&
                !disabled &&
                ReactDOM.createPortal(
                  <div
                    ref={popoverRef}
                    className="card"
                    style={{
                      position: "absolute",
                      top: `${popoverPos.top}px`,
                      left: `${popoverPos.left}px`,
                      zIndex: 99999,
                      width: "185px",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      border: "none",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                      overflow: "hidden",
                      padding: "8px",
                    }}
                  >
                    <style>{`
                      .time-picker-column::-webkit-scrollbar {
                        width: 0px;
                        background: transparent;
                      }
                      .time-item-hover:hover {
                        background-color: #f1f5f9 !important;
                        color: #0f172a !important;
                      }
                    `}</style>

                    {/* 2 Scrollable Columns with Infinite Circular Looping */}
                    <div className="d-flex" style={{ height: `${VISIBLE_HEIGHT}px` }}>
                      {/* Hours Column */}
                      <div
                        ref={hoursRef}
                        onScroll={handleHoursScroll}
                        className="flex-fill overflow-auto border-end px-1 time-picker-column"
                        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                      >
                        {HOURS_REPEAT.map((h, idx) => {
                          const isSelected = h === currentHour;
                          return (
                            <div
                              key={`h-${idx}`}
                              className={`text-center py-1 small d-flex align-items-center justify-content-center ${!isSelected ? "time-item-hover" : ""
                                }`}
                              style={{
                                height: `${ITEM_HEIGHT}px`,
                                cursor: "pointer",
                                borderRadius: "6px",
                                backgroundColor: isSelected ? "#0d6efd" : "transparent",
                                color: isSelected ? "#ffffff" : "#334155",
                                fontWeight: isSelected ? "600" : "500",
                                border: isSelected ? "1.5px solid #0a58ca" : "1.5px solid transparent",
                                boxShadow: isSelected ? "0 2px 6px rgba(13, 110, 253, 0.3)" : "none",
                                margin: "1px 0",
                                transition: "all 0.12s ease-in-out",
                              }}
                              onClick={() => handleHourSelect(h)}
                            >
                              {h}
                            </div>
                          );
                        })}
                      </div>

                      {/* Minutes Column */}
                      <div
                        ref={minutesRef}
                        onScroll={handleMinutesScroll}
                        className="flex-fill overflow-auto px-1 time-picker-column"
                        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                      >
                        {MINUTES_REPEAT.map((m, idx) => {
                          const isSelected = m === currentMinute;
                          return (
                            <div
                              key={`m-${idx}`}
                              className={`text-center py-1 small d-flex align-items-center justify-content-center ${!isSelected ? "time-item-hover" : ""
                                }`}
                              style={{
                                height: `${ITEM_HEIGHT}px`,
                                cursor: "pointer",
                                borderRadius: "6px",
                                backgroundColor: isSelected ? "#0d6efd" : "transparent",
                                color: isSelected ? "#ffffff" : "#334155",
                                fontWeight: isSelected ? "600" : "500",
                                border: isSelected ? "1.5px solid #0a58ca" : "1.5px solid transparent",
                                boxShadow: isSelected ? "0 2px 6px rgba(13, 110, 253, 0.3)" : "none",
                                margin: "1px 0",
                                transition: "all 0.12s ease-in-out",
                              }}
                              onClick={() => {
                                handleMinuteSelect(m);
                                setIsOpen(false);
                              }}
                            >
                              {m}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>,
                  document.body
                )}

              {errors?.[name] && (
                <FormFeedback className="d-block position-absolute">
                  {(errors[name] as any)?.message}
                </FormFeedback>
              )}
            </>
          );
        }}
      />
    </div>
  );
};

export default CommonTimePicker;
